import { Alert, Box, Snackbar, Stack, Tab, Tabs } from '@mui/material';
import { Refresh } from '@mui/icons-material';

import { ConfirmationDialog, FormTitleBox } from '../../common/components';
import {
  AccessDeniedState,
  ContractDebtDetailDialog,
  ContractDetailDialog,
  CouponDetailDialog,
  DashboardGroupsTable,
  DashboardOverviewTab,
  FundSection,
  InitialBalancesTab,
  LotteryTab,
  PreAdjudicationConfirmationContent,
  PreadjudicationsOverviewTab,
  PuntajeFilters,
  RankingTable,
  TabPanel,
} from './components';
import { PRE_ADJ_SECTIONS, TAB_ITEMS } from './constants/puntajeConstants';
import usePuntajePage from './hooks/usePuntajePage';

const Puntaje = () => {
  const {
    permissions: { isAdmin, canSeePuntaje },
    filters: {
      groups,
      loadingGroups,
      selectedGroupUuid,
      year,
      month,
      activeTab,
      activeRankingTab,
      activePreAdjudicationsTab,
      setYear,
      setMonth,
      setActiveTab,
      setActiveRankingTab,
      setActivePreAdjudicationsTab,
      handleSelectGroup,
    },
    dialogs: {
      snackbar,
      confirmationDialog,
      lotteryForm,
      initialBalanceDialogOpen,
      initialBalanceForm,
      initialBalanceDeleteDialog,
      contractDebtDialog,
      couponDetailDialog,
      contractDetailDialog,
      setConfirmationDialog,
      setLotteryForm,
      setInitialBalanceForm,
      handleCloseSnackbar,
      handleOpenConfirmation,
      handleCloseConfirmation,
      handleCloseInitialBalanceDelete,
      handleCloseContractDebtDetail,
      handleCloseCouponDetail,
      handleCloseContractDetail,
    },
    data: {
      dashboardRows,
      selectedDashboard,
      puntajeFund,
      sorteoFund,
      preAdjudicationsRows,
      financingInstallmentsCreated,
      contractDebtDetail,
      couponDetail,
      contractDetail,
    },
    paginations: {
      dashboardPagination,
      rankingPagination,
      eligibilityPagination,
      puntajeMovementsPagination,
      sorteoMovementsPagination,
      puntajePreAdjudicationsPagination,
      sorteoPreAdjudicationsPagination,
      desbordePreAdjudicationsPagination,
      lotteryPagination,
      initialBalancesPagination,
    },
    queries: {
      dashboardQuery,
      rankingQuery,
      eligibilityQuery,
      puntajeFundQuery,
      sorteoFundQuery,
      preAdjudicationsQuery,
      lotteryResultsQuery,
      initialBalancesQuery,
      contractDebtDetailQuery,
      couponDetailQuery,
      contractDetailQuery,
      isVisibleDataFetching,
    },
    mutations: {
      confirmMutation,
      syncLotteryMutation,
      upsertLotteryMutation,
      upsertInitialBalanceMutation,
      deleteInitialBalanceMutation,
    },
    handlers: {
      handleConfirmPreAdjudication,
      handleSaveLotteryResult,
      handleSyncLotteryResult,
      handleSaveInitialBalance,
      handleOpenInitialBalanceDialog,
      handleCloseInitialBalanceDialog,
      handleEditInitialBalance,
      handleOpenInitialBalanceDelete,
      handleDeleteInitialBalance,
      handleOpenContractDebtDetail,
      handleOpenCouponDetail,
      handleOpenContractDetail,
      handleRefreshVisibleData,
    },
  } = usePuntajePage();

  if (!canSeePuntaje) {
    return <AccessDeniedState />;
  }

  return (
    <>
      <Box>
        <FormTitleBox
          title="Puntaje | Habitat 99"
          showButton
          buttonText="Refrescar"
          buttonIcon={Refresh}
          onButtonClick={handleRefreshVisibleData}
          isLoading={isVisibleDataFetching}
          buttonProps={{
            variant: 'outlined',
          }}
        />

        {activeTab !== 6 && (
          <PuntajeFilters
            groups={groups}
            loadingGroups={loadingGroups}
            selectedGroupUuid={selectedGroupUuid}
            year={year}
            month={month}
            onSelectGroup={handleSelectGroup}
            onYearChange={setYear}
            onMonthChange={setMonth}
          />
        )}

        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            px: 1,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {TAB_ITEMS.map((tab) => {
              const Icon = tab.icon;
              return (
                <Tab
                  key={tab.label}
                  icon={<Icon fontSize="small" />}
                  iconPosition="start"
                  label={tab.label}
                  sx={{ textTransform: 'none' }}
                />
              );
            })}
          </Tabs>
        </Box>

        <TabPanel activeTab={activeTab} index={0}>
          <DashboardOverviewTab
            dashboardRows={dashboardRows}
            selectedDashboard={selectedDashboard}
            financingInstallmentsCreated={financingInstallmentsCreated}
            isAdmin={isAdmin}
            onConfirmSuggestion={handleOpenConfirmation}
            confirmLoading={confirmMutation.isPending}
          />
        </TabPanel>

        <TabPanel activeTab={activeTab} index={1}>
          <DashboardGroupsTable
            dashboardRows={dashboardPagination.data}
            selectedGroupUuid={selectedGroupUuid}
            onSelectGroup={handleSelectGroup}
            isLoading={dashboardQuery.isLoading}
            pagination={dashboardPagination}
          />
        </TabPanel>

        <TabPanel activeTab={activeTab} index={2}>
          <FundSection
            title="Fondo Puntaje"
            summary={puntajeFund.summary}
            movements={puntajeMovementsPagination.data}
            isLoading={puntajeFundQuery.isLoading}
            isAdmin={isAdmin}
            onConfirmSuggestion={() =>
              handleOpenConfirmation(puntajeFund.summary?.active_suggestion)
            }
            confirmLoading={confirmMutation.isPending}
            pagination={puntajeMovementsPagination}
            onCouponClick={handleOpenCouponDetail}
            onContractClick={handleOpenContractDetail}
            showScoreColumn
          />
        </TabPanel>

        <TabPanel activeTab={activeTab} index={3}>
          <FundSection
            title="Fondo Sorteo"
            summary={sorteoFund.summary}
            movements={sorteoMovementsPagination.data}
            isLoading={sorteoFundQuery.isLoading}
            isAdmin={isAdmin}
            onConfirmSuggestion={() =>
              handleOpenConfirmation(sorteoFund.summary?.active_suggestion)
            }
            confirmLoading={confirmMutation.isPending}
            pagination={sorteoMovementsPagination}
            onCouponClick={handleOpenCouponDetail}
            onContractClick={handleOpenContractDetail}
          />
        </TabPanel>

        <TabPanel activeTab={activeTab} index={4}>
          <Stack>
            <Box
              sx={{
                bgcolor: 'background.paper',
                px: 1,
              }}
            >
              <Tabs
                value={activeRankingTab}
                onChange={(_, value) => setActiveRankingTab(value)}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Ranking adjudicable por grupo" sx={{ textTransform: 'none' }} />
                <Tab label="Elegibilidad base del grupo" sx={{ textTransform: 'none' }} />
              </Tabs>
            </Box>

            <TabPanel activeTab={activeRankingTab} index={0}>
              <RankingTable
                title="Ranking adjudicable por grupo"
                rows={rankingPagination.data}
                isLoading={rankingQuery.isLoading}
                pagination={rankingPagination}
              />
            </TabPanel>

            <TabPanel activeTab={activeRankingTab} index={1}>
              <RankingTable
                title="Elegibilidad base del grupo"
                rows={eligibilityPagination.data}
                isLoading={eligibilityQuery.isLoading}
                pagination={eligibilityPagination}
                onOpenDebtDetail={handleOpenContractDebtDetail}
                debtDetailLoadingContractUuid={
                  contractDebtDetailQuery.isFetching ? contractDebtDialog.row?.uuid_contract : null
                }
              />
            </TabPanel>
          </Stack>
        </TabPanel>

        <TabPanel activeTab={activeTab} index={5}>
          <Stack spacing={0}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                px: 1,
              }}
            >
              <Tabs
                value={activePreAdjudicationsTab}
                onChange={(_, value) => setActivePreAdjudicationsTab(value)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {PRE_ADJ_SECTIONS.map((section) => (
                  <Tab key={section.key} label={section.title} sx={{ textTransform: 'none' }} />
                ))}
              </Tabs>
            </Box>

            <PreadjudicationsOverviewTab
              preAdjudicationsRows={preAdjudicationsRows}
              isLoading={preAdjudicationsQuery.isLoading}
              activeTab={activePreAdjudicationsTab}
              paginations={{
                PUNTAJE: puntajePreAdjudicationsPagination,
                SORTEO: sorteoPreAdjudicationsPagination,
                DESBORDE: desbordePreAdjudicationsPagination,
              }}
            />
          </Stack>
        </TabPanel>

        <TabPanel activeTab={activeTab} index={6}>
          <LotteryTab
            isAdmin={isAdmin}
            lotteryRows={lotteryPagination.data}
            lotteryForm={lotteryForm}
            setLotteryForm={setLotteryForm}
            isLoading={lotteryResultsQuery.isLoading}
            pagination={lotteryPagination}
            syncLoading={syncLotteryMutation.isPending}
            saveLoading={upsertLotteryMutation.isPending}
            onSync={handleSyncLotteryResult}
            onSave={handleSaveLotteryResult}
          />
        </TabPanel>

        <TabPanel activeTab={activeTab} index={7}>
          <InitialBalancesTab
            isAdmin={isAdmin}
            groups={groups}
            initialBalanceRows={initialBalancesPagination.data}
            initialBalanceDialogOpen={initialBalanceDialogOpen}
            initialBalanceForm={initialBalanceForm}
            setInitialBalanceForm={setInitialBalanceForm}
            isLoading={initialBalancesQuery.isLoading}
            pagination={initialBalancesPagination}
            saveLoading={upsertInitialBalanceMutation.isPending}
            deleteLoading={deleteInitialBalanceMutation.isPending}
            onOpenDialog={handleOpenInitialBalanceDialog}
            onCloseDialog={handleCloseInitialBalanceDialog}
            onSave={handleSaveInitialBalance}
            onEdit={handleEditInitialBalance}
            onDelete={handleOpenInitialBalanceDelete}
          />
        </TabPanel>
      </Box>

      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmPreAdjudication}
        title="Confirmar preadjudicacion"
        message="Esta accion persiste la preadjudicacion, actualiza el contrato y debita el fondo correspondiente."
        confirmText="Confirmar"
        cancelText="Cancelar"
        confirmVariant="base"
        isLoading={confirmMutation.isPending}
        confirmDisabled={!confirmationDialog.suggestion || !confirmationDialog.preAdjudicationDate}
        details={
          <PreAdjudicationConfirmationContent
            suggestion={confirmationDialog.suggestion}
            preAdjudicationDate={confirmationDialog.preAdjudicationDate}
            onPreAdjudicationDateChange={(event) =>
              setConfirmationDialog((prev) => ({
                ...prev,
                preAdjudicationDate: event.target.value,
              }))
            }
            observations={confirmationDialog.observations}
            onObservationsChange={(event) =>
              setConfirmationDialog((prev) => ({
                ...prev,
                observations: event.target.value,
              }))
            }
          />
        }
      />

      <ConfirmationDialog
        open={initialBalanceDeleteDialog.open}
        onClose={handleCloseInitialBalanceDelete}
        onConfirm={handleDeleteInitialBalance}
        title="Eliminar saldo inicial"
        message="Esta accion desactiva el saldo inicial y elimina su movimiento base del fondo."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmVariant="base"
        isLoading={deleteInitialBalanceMutation.isPending}
        confirmDisabled={!initialBalanceDeleteDialog.row}
        details={
          initialBalanceDeleteDialog.row ? (
            <Box sx={{ pt: 1 }}>
              Se eliminara el saldo {initialBalanceDeleteDialog.row.fund_type} del grupo{' '}
              {initialBalanceDeleteDialog.row.group_name || '-'} para el año{' '}
              {initialBalanceDeleteDialog.row.year}.
            </Box>
          ) : null
        }
      />

      <ContractDebtDetailDialog
        open={contractDebtDialog.open}
        onClose={handleCloseContractDebtDetail}
        debtDetail={contractDebtDetail}
        isLoading={contractDebtDetailQuery.isLoading || contractDebtDetailQuery.isFetching}
        error={contractDebtDetailQuery.error}
        contractRow={contractDebtDialog.row}
      />

      <CouponDetailDialog
        open={couponDetailDialog.open}
        onClose={handleCloseCouponDetail}
        couponDetail={couponDetail}
        isLoading={couponDetailQuery.isLoading || couponDetailQuery.isFetching}
        error={couponDetailQuery.error}
      />

      <ContractDetailDialog
        open={contractDetailDialog.open}
        onClose={handleCloseContractDetail}
        contractDetail={contractDetail}
        isLoading={contractDetailQuery.isLoading || contractDetailQuery.isFetching}
        error={contractDetailQuery.error}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Puntaje;
